const BASE = import.meta.env.VITE_API_URL || 'https://linebrkr-deploy-production.up.railway.app'

// Private access gate: the code the user typed on the lock screen (if any) is sent
// on every request; when the API is locked it 401s and we raise the lock screen.
const _access = () => { try { return localStorage.getItem('brkr_access') || '' } catch (_) { return '' } }
export const setAccessCode = (c) => { try { localStorage.setItem('brkr_access', c || '') } catch (_) {} }
const _hdrs = (h = {}) => { const c = _access(); return c ? { ...h, 'X-Access-Code': c } : h }
function _check(res, path) {
  if (res.status === 401) { try { window.dispatchEvent(new CustomEvent('brkr-locked')) } catch (_) {} }
  if (!res.ok) throw new Error(`API error ${res.status}: ${path}`)
  return res.json()
}

async function get(path) {
  const res = await fetch(`${BASE}${path}`, { headers: _hdrs() })
  return _check(res, path)
}

async function post(path, body = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: _hdrs({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body)
  })
  return _check(res, path)
}

// ── Free tier (email → capped free board) ─────────────────────────────────────
// Direct fetch (NOT get()/post()) so a 401 never trips the lock screen. Uses the shared
// 'brkr_free' token slot (one free account works across sports) and the 'brkr_ref' tag for
// referral attribution. Endpoints live under the ungated /free/* surface.
export const getFreeToken = () => { try { return localStorage.getItem('brkr_free') || '' } catch (_) { return '' } }
export const setFreeToken = (t) => { try { localStorage.setItem('brkr_free', (t || '').trim()) } catch (_) {} }
// Persist a referral code a user typed in (not only arrived with via ?ref=), so it rides the
// shared brkr_ref slot through the free signup and the paid checkout — for any sport.
export const setRef = (code) => { try { const c = (code || '').trim(); if (c) localStorage.setItem('brkr_ref', c) } catch (_) {} }
export async function mlbFreeSignup(email, refOverride) {
  let ref = ''; try { ref = localStorage.getItem('brkr_ref') || '' } catch (_) {}
  if (refOverride && refOverride.trim()) { ref = refOverride.trim(); setRef(ref) }
  const r = await fetch(`${BASE}/free/mlb-signup`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, ref }) })
  const d = await r.json().catch(() => ({}))
  if (!r.ok || !d.token) throw new Error(d.detail || 'Could not sign up. Try again.')
  setFreeToken(d.token)
  return d
}
export async function getMLBFreeBoard() {
  try {
    const r = await fetch(`${BASE}/free/mlb-board?token=${encodeURIComponent(getFreeToken())}`)
    return r.ok ? await r.json() : { plays: [] }
  } catch (_) { return { plays: [] } }
}

// ── Admin: beta-code management (owner-only) ──────────────────────────────────
// Sends the separate ADMIN code (and the access code, in case the admin routes
// fall back to it). A bad admin code throws here but must NOT lock the whole app,
// so these use their own checker that does not fire the lock-screen event.
const _admin = () => { try { return localStorage.getItem('brkr_admin') || '' } catch (_) { return '' } }
export const setAdminCode = (c) => { try { localStorage.setItem('brkr_admin', c || '') } catch (_) {} }
const _ahdrs = (h = {}) => { const o = { ...h }; const a = _admin(); if (a) o['X-Admin-Code'] = a; const c = _access(); if (c) o['X-Access-Code'] = c; return o }
function _acheck(res, path) { if (!res.ok) throw new Error(`API error ${res.status}: ${path}`); return res.json() }
async function _aget(path) { const res = await fetch(`${BASE}${path}`, { headers: _ahdrs() }); return _acheck(res, path) }
async function _apost(path, body = {}) { const res = await fetch(`${BASE}${path}`, { method: 'POST', headers: _ahdrs({ 'Content-Type': 'application/json' }), body: JSON.stringify(body) }); return _acheck(res, path) }
export const listBetaCodes  = () => _aget('/admin/codes')
export const createBetaCode = (label) => _apost('/admin/codes', { label })
export const revokeBetaCode = (id) => _apost(`/admin/codes/${id}/revoke`)
export const enableBetaCode = (id) => _apost(`/admin/codes/${id}/enable`)

// ── Referrals (cross-sport) — the tally + reward program is served by /nfl/admin/* but the
// data spans every sport. Owner calls pass ?key= (the master admin/access code); the user's
// own card rides the X-Access-Code header.
const _refKey = () => _admin() || _access()
export async function getReferralsAdmin() {
  try { const r = await fetch(`${BASE}/nfl/admin/referrals?format=json&key=${encodeURIComponent(_refKey())}`)
    const b = await r.json().catch(() => ({})); return r.ok ? b : { _error: r.status, referrers: [] }
  } catch (e) { return { _error: String(e), referrers: [] } }
}
export async function referralPayout(code, amount, note = '') {
  try { const r = await fetch(`${BASE}/nfl/admin/referral-payout?key=${encodeURIComponent(_refKey())}&code=${encodeURIComponent(code)}&amount=${encodeURIComponent(amount)}&note=${encodeURIComponent(note)}`)
    const b = await r.json().catch(() => ({})); return r.ok ? b : { ok: false, detail: b.detail || r.status }
  } catch (e) { return { ok: false, detail: String(e) } }
}
export async function makeRefCode(code, label = '') {
  try { const r = await fetch(`${BASE}/nfl/admin/referral-code?key=${encodeURIComponent(_refKey())}&code=${encodeURIComponent(code)}&label=${encodeURIComponent(label)}`)
    const b = await r.json().catch(() => ({})); return r.ok ? b : { ok: false, detail: b.detail || r.status }
  } catch (e) { return { ok: false, detail: String(e) } }
}
export async function attributeReferral(code, name, qualified = true, note = '') {
  try { const r = await fetch(`${BASE}/nfl/admin/referral-attribute?key=${encodeURIComponent(_refKey())}&code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}&qualified=${qualified ? 1 : 0}&note=${encodeURIComponent(note)}`)
    const b = await r.json().catch(() => ({})); return r.ok ? b : { ok: false, detail: b.detail || r.status }
  } catch (e) { return { ok: false, detail: String(e) } }
}
// The signed-in user's own referral card (code + reward progress). Works for a paid user via
// the access-code header, or a free user via their stored free token.
// Affiliate book links (book_key → tracked URL); empty until set in Railway. Shared endpoint.
export async function getBookLinks() {
  try { const r = await fetch(`${BASE}/nfl/book-links`); return r.ok ? await r.json() : {} }
  catch (_) { return {} }
}
export async function getReferral() {
  try { const r = await fetch(`${BASE}/nfl/free/referral?token=${encodeURIComponent(getFreeToken())}`, { headers: _ahdrs() })
    return r.ok ? await r.json() : { ref_code: '', referred: 0 }
  } catch (_) { return { ref_code: '', referred: 0 } }
}

// Admin one-shots — auth rides on the saved X-Admin-Code header (no ?key= needed).
export const fixBatsHandAll    = () => _aget('/free/fix-bats-hand?all=1')
export const fixPitcherHandsAll = () => _aget('/free/fix-pitcher-hand?all=1')
// Manual override — set one pitcher's throwing hand directly (no MLB lookup), for a
// rookie whose stats row has no MLB id. hand = 'L' | 'R'.
export const setPitcherHand = (name, hand) => _aget(`/free/fix-pitcher-hand?name=${encodeURIComponent(name)}&hand=${hand}`)
export const reslateBoard   = () => _aget('/free/reslate')
export const refreshLineups = () => _aget('/free/refresh-lineups')
// Scratch a player from today's board — deletes ALL of that player's prop rows for
// the date (every bet type), so a pre-game injury/scratch drops off the board and out
// of the graded record immediately. names = "First Last" (comma-separated for several).
export const scratchPlayer  = (names) => _aget(`/free/scratch?names=${encodeURIComponent(names)}`)
// Undo an accidental scratch — re-project just that player (no whole-slate rebuild).
export const readdPlayer    = (names) => _aget(`/free/readd-player?names=${encodeURIComponent(names)}`)
export const syncRosters    = (player='') => _aget(`/free/sync-rosters${player?`?player=${encodeURIComponent(player)}`:''}`)
export const pullOdds       = () => _aget('/free/pull-odds')
export const getOddsStatus  = () => _aget('/free/odds-status')

// ── Billing (Stripe subscriptions) ───────────────────────────────────────────
// OPEN calls — a buyer has no access code yet, so they never send one and must NOT
// trip the lock screen. startCheckout returns a hosted Stripe URL to redirect to;
// claimSession exchanges the post-checkout session_id for the access code.
// Referral capture: a friend's link is linebrkr.app/?ref=mike. Persist the tag the
// first time we see it so it survives the whole funnel (browse → trial → checkout),
// and read it back at checkout time. Sanitized to lowercase alphanumerics/-/_.
export const captureRef = () => {
  try {
    const q = new URLSearchParams(window.location.search).get('ref') || ''
    const clean = q.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40)
    if (clean) localStorage.setItem('brkr_ref', clean)
  } catch (_) {}
}
const getRef = () => { try { return localStorage.getItem('brkr_ref') || '' } catch (_) { return '' } }

export const startCheckout = async ({ plan = 'pro', founder = false, email = null } = {}) => {
  const res = await fetch(`${BASE}/billing/checkout`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan, founder, email, ref: getRef() || undefined })
  })
  if (!res.ok) {
    // Surface the server's plain-English reason (e.g. Founder sold out) instead of a bare code.
    let detail = `checkout ${res.status}`
    try { const j = await res.json(); if (j?.detail) detail = j.detail } catch (_) {}
    throw new Error(detail)
  }
  return res.json()   // { url }
}
// Live Founder scarcity: { cap, taken, remaining, sold_out, price_configured }. Open route.
export const getFoundersStatus = async () => {
  const res = await fetch(`${BASE}/billing/founders-status`)
  if (!res.ok) throw new Error(`founders ${res.status}`)
  return res.json()
}
export const claimSession = async (session_id) => {
  const res = await fetch(`${BASE}/billing/claim`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id })
  })
  if (!res.ok) throw new Error(`claim ${res.status}`)
  return res.json()   // { code, status, plan }
}
// Current access tier for the stored code ('free' | 'rookie' | 'pro'). Sends the
// access code; open route, never trips the lock screen.
export const getPlan = async () => {
  const res = await fetch(`${BASE}/billing/me`, { headers: _hdrs() })
  if (!res.ok) throw new Error(`plan ${res.status}`)
  return res.json()   // { plan }
}
export const setPlanLocal = (p) => { try { localStorage.setItem('brkr_plan', p || '') } catch (_) {} }
export const getPlanLocal = () => { try { return localStorage.getItem('brkr_plan') || '' } catch (_) { return '' } }
// Account summary for the signed-in code: { plan, status, email, can_manage, source }.
// Sends the access code; open route (never trips the lock screen). can_manage=true means
// a self-serve Manage/Cancel button will work (the code is tied to a Stripe customer).
export const getSubscription = async () => {
  const res = await fetch(`${BASE}/billing/subscription`, { headers: _hdrs() })
  if (!res.ok) throw new Error(`subscription ${res.status}`)
  return res.json()
}
// Open the Stripe Customer Portal (update card, view invoices, CANCEL). Sends the access
// code; returns { url } to redirect to. Throws the server's plain-English reason on 401/404/503
// so the UI can tell the user exactly why (not signed in / comp code / portal not enabled).
export const openBillingPortal = async () => {
  const res = await fetch(`${BASE}/billing/portal`, {
    method: 'POST', headers: _hdrs({ 'Content-Type': 'application/json' }), body: '{}'
  })
  if (!res.ok) {
    let detail = `portal ${res.status}`
    try { const j = await res.json(); if (j?.detail) detail = j.detail } catch (_) {}
    throw new Error(detail)
  }
  return res.json()   // { url }
}
// Open Free-tier landing data (today's top play + track record). No code needed.
export const getFreePreview = async () => {
  const res = await fetch(`${BASE}/free/preview`)
  if (!res.ok) throw new Error(`free ${res.status}`)
  return res.json()   // { top_play, record }
}

// Pitcher-data health drawer: every current-season pitcher (ERA/WHIP/K9/HR9/IP/GS)
// with health flags (stale, defaulted, small-sample-regressed). /free bypasses the gate.
export const getPitcherData = async (team = '') => {
  // starters=0 → include the bullpen too (the panel orders starters first, relievers after).
  const q = `?all=1&starters=0${team ? `&team=${encodeURIComponent(team)}` : ''}`
  const res = await fetch(`${BASE}/free/pitcher-data${q}`)
  if (!res.ok) throw new Error(`pitcher-data ${res.status}`)
  return res.json()   // { summary, flagged, rows }
}

// Game-outcome win-prob tracker: record, win%, Brier, calibration + recent settled games.
export const getGameOutcomeRecord = async (days = 30, recent = 20) => {
  const res = await fetch(`${BASE}/free/game-outcome-record?days=${days}&recent=${recent}`)
  if (!res.ok) throw new Error(`game-outcome ${res.status}`)
  return res.json()   // { scorecard, recent }
}

// Players with a birthday today (from the MLB feed), flagged if on today's board.
export const getBirthdays = async () => {
  const res = await fetch(`${BASE}/free/birthdays`)
  if (!res.ok) throw new Error(`birthdays ${res.status}`)
  return res.json()   // { date, count, birthdays:[{player,team,position,age,on_board}] }
}

// Ballpark promotions/theme days today (bobblehead nights, giveaways, fireworks).
export const getBallparkEvents = async () => {
  const res = await fetch(`${BASE}/free/ballpark-events`)
  if (!res.ok) throw new Error(`ballpark-events ${res.status}`)
  return res.json()   // { date, count, events:[{name,type,team,matchup,venue,on_board,is_bobblehead}] }
}

// Ongoing HR-model validation: actual HR rate bucketed by Barrel% and by projected prob,
// accumulating from settled results.
export const getHrValidation = async (days = 60) => {
  const res = await fetch(`${BASE}/free/hr-validation?days=${days}`)
  if (!res.ok) throw new Error(`hr-validation ${res.status}`)
  return res.json()   // { n, hr, base_rate, by_barrel:[...], by_prob:[...] }
}

// Per-market calibration: projected vs actual hit rate + gap, per market. basis:
// 'calibrated' (default) grades the number the board actually SHOWS (post-calibration);
// 'raw' grades the untouched model output (Admin diagnostic).
export const getMarketCalibration = async (days = 90, basis = 'calibrated') => {
  const res = await fetch(`${BASE}/free/market-calibration?days=${days}&basis=${basis}`)
  if (!res.ok) throw new Error(`market-calibration ${res.status}`)
  return res.json()   // { basis, markets: { [prop_type]: {label,n,hit_rate,predicted,overall_gap,bands} } }
}

// Season-long regression backtest of the CURRENT game model: re-runs project_game over
// every settled game in range, grades vs finals → accuracy, Brier, calibration table.
export const getGameModelBacktest = async ({ start = '', end = '', limit = 0 } = {}) => {
  const q = new URLSearchParams()
  if (start) q.set('start', start)
  if (end) q.set('end', end)
  if (limit) q.set('limit', String(limit))
  const res = await fetch(`${BASE}/free/game-model-backtest?${q.toString()}`)
  if (!res.ok) throw new Error(`game-model-backtest ${res.status}`)
  return res.json()   // { n, record, win_pct, brier, calibration:[...], caveat, games:[...] }
}

// Strikeout analytics bundle for the K drawer: recency-blended k9/IP, swing-and-miss
// stuff, opposing-lineup K rate + tag, arsenal.
export const getKAnalytics = async (name, opp = '') => {
  const q = new URLSearchParams({ name })
  if (opp) q.set('opp', opp)
  const res = await fetch(`${BASE}/free/k-analytics?${q.toString()}`)
  if (!res.ok) throw new Error(`k-analytics ${res.status}`)
  return res.json()   // { name, throws, season, form, stuff, arsenal, opponent }
}

// Side-tracked strikeouts (K) hit/miss record — settled results, floored at the K epoch.
export const getKRecord = async (days = 60, recent = 25) => {
  const res = await fetch(`${BASE}/free/k-record?days=${days}&recent=${recent}`)
  if (!res.ok) throw new Error(`k-record ${res.status}`)
  return res.json()   // { n, wins, losses, hit_rate, since, recent:[...] }
}

// Head-to-head Matchup Breakdown: one hitter vs the opposing starter for a date —
// batter + pitcher factor lines (each with a good/bad lean), park, and the model's
// prob + Rating for every market, plus a plain-English read. Open /free route.
// Returns the JSON even on a 404 (clean {error} body) so the caller can show a tidy
// "no projections" state instead of throwing.
export const getMatchup = async (player, date = '') => {
  const q = new URLSearchParams({ player: String(player) })
  if (date) q.set('date', date)
  const res = await fetch(`${BASE}/free/matchup?${q.toString()}`)
  let data = null
  try { data = await res.json() } catch (_) { data = null }
  if (data && typeof data === 'object') return data
  throw new Error(`matchup ${res.status}`)
}

// Projected matchup — pick ANY hitter vs ANY pitcher, model re-runs the projectors
// live against the chosen arm (Pro). Returns the same shape as getMatchup plus
// pitch_matchup / pitch_edge / projected / neutral_park.
export const getProjectedMatchup = async (player, pitcher, date = '') => {
  const q = new URLSearchParams({ player: String(player), pitcher: String(pitcher) })
  if (date) q.set('date', date)
  const res = await fetch(`${BASE}/free/projected-matchup?${q.toString()}`, { headers: _hdrs() })
  let data = null
  try { data = await res.json() } catch (_) { data = null }
  if (data && typeof data === 'object') return data
  throw new Error(`projected-matchup ${res.status}`)
}

// Pitcher intel card: season line, home/away + vs-L/R splits, statcast, recent starts.
export const getPitcherIntel = async (name) => {
  const res = await fetch(`${BASE}/free/pitcher-intel?name=${encodeURIComponent(name)}`)
  if (!res.ok) throw new Error(`pitcher-intel ${res.status}`)
  return res.json()
}

// Today's game-outcome model: projected winner, win%, fair ML, proj runs, tier, why.
export const getGamesModel = async () => {
  // Cache-bust + no-store: this was the ONE call using a bare fetch, so Safari kept
  // serving a stale/empty games-export response (captured before the slate filled in)
  // and the Model tab showed "No game projections yet" while the server had 15 games.
  const res = await fetch(`${BASE}/free/games-export?fmt=json&_t=${Date.now()}`,
    { headers: _hdrs(), cache: 'no-store' })
  if (!res.ok) throw new Error(`games-model ${res.status}`)
  return res.json()   // { date, games, outcomes:[...] }
}

// ── Leaderboard ──────────────────────────────────────────────────────────────
export const getLeaderboard = (propType, date, limit = 200, confirmedOnly = true) =>
  get(`/projections/hrr/leaderboard?prop_type=${propType}&game_date=${date}&limit=${limit}&confirmed_only=${confirmedOnly}`)

// Best same-team correlated stacks for the slate — built to maximize a PERFECT day
// (every leg hits), not per-leg EV. Returns top teams ranked by joint hit floor.
export const getPerfectStack = (date, propType = 'HRR_over_1.5', size = 3) =>
  get(`/projections/perfect-stack?game_date=${date}&prop_type=${propType}&size=${size}`)

// "Perfect storm": hitters where pitcher-vs-hand + zone fit + form all line up.
// requireEdge=true keeps only spots whose calibrated prob beats the book price.
// hitterPark=true keeps only hitter-friendly parks (park factor >= 1.0).
export const getPerfectStorm = (date, requireEdge = false, limit = 200, hitterPark = false) =>
  get(`/projections/perfect-storm?game_date=${date}&limit=${limit}${requireEdge ? '&require_edge=true' : ''}${hitterPark ? '&min_park=1.0' : ''}`)

// Backtest of the storm factors over settled history (does the premise hold?).
export const getStormBacktest = (days = 45) =>
  get(`/projections/perfect-storm/backtest?days=${days}`)

// Forward-posted HRR Storm record — frozen daily picks graded vs settled results.
export const getStormRecord = (days = 45) =>
  get(`/free/storm-record?days=${days}`)

// Cumulative Power Spots track record — every snapshot graded on anytime HR (+ ROI).
export const getPowerSpotsRecord = () =>
  get(`/free/power-spots-record?fmt=json`)

// Projected home runs for the slate (total + per game) — sum of batter HR probs.
export const getSlateHrProjection = (date = '') =>
  get(`/free/slate-hr-projection${date ? `?date=${date}` : ''}`)

// Accuracy backtest of the slate HR projection — projected vs actual over N days.
export const getSlateHrBacktest = (days = 30) =>
  get(`/free/slate-hr-backtest?days=${days}`)

// Homer-pick accuracy — our top HR board's hit-rate vs the league base (lift).
export const getHrPickRecord = (days = 45, top = 10) =>
  get(`/free/hr-pick-record?days=${days}&top=${top}`)

// 2-leg parlay record (best 2 picks + best same-team pair) with day-by-day W/L.
// `since` (ISO date) pins the window start; omit for the full season.
export const getTwoLegRecord = (since = '') =>
  get(`/scorecard/two-leg-record?format=json${since ? `&since=${since}` : ''}`)

// Closing Line Value — did our number beat the closing line (proof of edge).
export const getClv = (days = 30) =>
  get(`/scorecard/clv?days=${days}&format=json`)

// Per-day picks with full Confidence inputs (zone_fit, L7 form, park, pitcher_adj…),
// already batch-loaded + cached server-side. Powers the Analytics tab.
export const getToday = (propType, date, limit = 500) =>
  get(`/projections/today?prop_type=${propType}&game_date=${date}&limit=${limit}`)

// One player's row for a single market, with the full analytics inputs block — same
// source as the grid, scoped server-side. Powers the player card's per-bet-type
// Analytics sub-tab. Returns the /today payload filtered to this player (0 or 1 row).
export const getPlayerMarketAnalytics = (playerId, propType, date) =>
  get(`/projections/today?prop_type=${encodeURIComponent(propType)}&player_id=${playerId}&game_date=${date}&limit=1`)

export const getBookOdds = (playerId, propType, date) =>
  get(`/projections/book-odds?player_id=${playerId}&prop_type=${encodeURIComponent(propType)}&game_date=${date}`)

// ── Hot Corner (batter-vs-pitcher) ────────────────────────────────────────────
export const getHotCorner = (date, { hrOnly = false, team = '' } = {}) =>
  get(`/bvp/hot-corner?game_date=${date}&hr_only=${hrOnly}${team ? `&team=${encodeURIComponent(team)}` : ''}`)

// Player Trends: per-window (L5/L10/L20/SZN/H2H) prop hit-rate grid for the slate.
// line is optional (backend defaults per market); sort picks the ranking window.
export const getTrends = (date, { market = 'hrr', line = null, team = '', sort = 'L10', minGames = 1 } = {}) => {
  const q = new URLSearchParams({ market, game_date: date, sort, min_games: String(minGames) })
  if (line != null) q.set('line', String(line))
  if (team) q.set('team', team)
  return get(`/trends?${q.toString()}`)
}

// Daily de-parked HR signal board — top hitters ranked by true homer signal
// (bat + matchup), park excluded from the score.
export const getHrSignals = (date, top = 25) =>
  get(`/free/hr-signals?date=${date}&top=${top}`)

// Pro intuitive search — plain-English query → matching hitters off today's board.
export const getNlSearch = (q, date = '') =>
  get(`/free/nl-search?q=${encodeURIComponent(q)}${date ? `&date=${date}` : ''}`)

// Beat-the-Book value board — plays ranked by de-vigged model-vs-book edge (Pro)
export const getValueBoard = (minEdge = 0.02, date = '') =>
  get(`/free/value-board?min_edge=${minEdge}${date ? `&date=${date}` : ''}`)

// ── Scorecard ────────────────────────────────────────────────────────────────
export const getDailyScorecard = (date) =>
  get(`/scorecard/daily?game_date=${date}`)

export const getScorecardSummary = (days = 30) =>
  get(`/scorecard/summary?days=${days}`)

// Postseason-only record (public passthrough; same top-10/25 engine, scoped to playoff dates)
export const getPlayoffRecord = (days = 120) =>
  get(`/free/playoff-record?days=${days}`)

export const getTrustAnchor = (days = 30) =>
  get(`/stats/trust-anchor?days=${days}`)

export const getScorecardTrend = (days = 14, propType = 'HRR_over_1.5') =>
  get(`/scorecard/trend?days=${days}&prop_type=${encodeURIComponent(propType)}`)

export const getTrending = (days = 7, limit = 8) =>
  get(`/stats/trending?days=${days}&limit=${limit}`)

export const getEliteRecord = (days = 30, threshold = 0.70) =>
  get(`/scorecard/elite-record?days=${days}&threshold=${threshold}`)

export const getCalibration = (date, propType = 'HRR_over_1.5') =>
  get(`/scorecard/calibration?game_date=${date}&prop_type=${propType}`)

export const getConsensus = (date) =>
  get(`/scorecard/consensus?game_date=${date}`)

export const runSnapshot = (date) =>
  post(`/scorecard/snapshot?game_date=${date}`)

export const runSettle = (date) =>
  post(`/scorecard/settle?game_date=${date}`)

export const runValidation = (date) =>
  post(`/scorecard/run?game_date=${date}`)

// ── Players ──────────────────────────────────────────────────────────────────
export const searchPlayers = (q) =>
  get(`/players/?search=${encodeURIComponent(q)}&limit=10`)

export const getPlayerProps = (playerId, date) =>
  get(`/players/${playerId}/props?game_date=${date}`)

export const getPlayerGamelogs = (playerId, season = 2026, limit = 20) =>
  get(`/players/${playerId}/gamelogs?season=${season}&limit=${limit}`)

// Official vs-RHP / vs-LHP hitting splits (MLB Stats API), this season.
export const getPlayerSplits = (playerId, season = 2026) =>
  get(`/players/${playerId}/splits?season=${season}`)

export const getPlayerVsPitcher = (playerId, pitcherName) =>
  get(`/players/${playerId}/vs_pitcher?pitcher_name=${encodeURIComponent(pitcherName)}`)

export const getPlayerContactLog = (playerId, days = 15, limit = 25) =>
  get(`/players/${playerId}/contact-log?days=${days}&limit=${limit}`)

export const getPlayerInsights = (playerId, gameDate, propType) => {
  const q = new URLSearchParams()
  if (gameDate) q.set('game_date', gameDate)
  if (propType) q.set('prop_type', propType)
  const qs = q.toString()
  return get(`/players/${playerId}/insights${qs ? `?${qs}` : ''}`)
}

// ── Health ───────────────────────────────────────────────────────────────────
export const getHealth = () =>
  get('/health/')

// ── Admin diagnostics ─────────────────────────────────────────────────────────
// Per-market formula health: is every bet type actually running & returning?
export const getCoverage = (date = '') =>
  get(`/projections/coverage${date ? `?game_date=${date}` : ''}`)

// Per-market Validation Scorecard: calibration + CLV + ROI + verdict per bet type.
export const getValidation = (days = 45) =>
  get(`/scorecard/validation?days=${days}&format=json`)

// Daily Learnings digest: ranked "what to fix today" from settled results.
export const getLearnings = (days = 14) =>
  get(`/scorecard/learnings?days=${days}`)

// Near-live in-game stat lines (H/R/RBI/HR/TB + HRR) per player, from MLB boxscores.
export const getLive = (date = '') =>
  get(`/scorecard/live${date ? `?game_date=${date}` : ''}`)

// Catch-all sanity auditor: scans today's whole slate and flags anything wrong
// (park out of range, TBD-pitcher inflation, clamp squashing, degenerate probs…).
export const getAudit = (date = '') =>
  get(`/projections/audit${date ? `?game_date=${date}` : ''}`)

// Park / run-environment inflation for ALL teams — who the model over/under-boosts.
export const getTeamAudit = (days = 30) =>
  get(`/projections/team-audit?days=${days}`)

// ── Slate-day helper ─────────────────────────────────────────────────────────
// The board "day" rolls at 2 AM ET — AFTER nightly settlement — not at midnight.
// So between midnight and 2 AM the app keeps showing the slate that just played
// (which is what you're still settling/reviewing) instead of jumping to the next
// day's empty, lineup-less board. At 2 AM it flips to the new day. Achieved by
// reading the ET wall clock and subtracting 2 hours before taking the date.
export const todayStr = () => {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
  et.setHours(et.getHours() - 2)
  const y = et.getFullYear()
  const m = String(et.getMonth() + 1).padStart(2, '0')
  const d = String(et.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ET "yesterday" (the day the settled results belong to), same offset logic as todayStr.
export const yesterdayStr = () => {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
  et.setHours(et.getHours() - 2)
  et.setDate(et.getDate() - 1)
  const y = et.getFullYear()
  const m = String(et.getMonth() + 1).padStart(2, '0')
  const d = String(et.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const getResultsDaily = (date, propType='HRR_over_1.5', tier='top10', limit=50) =>
  get(`/scorecard/results/players?game_date=${date}&prop_type=${propType}&tier=${tier}&limit=${limit}`)

// Grid Standouts track record: how the board's frozen 🔥strong / ⚡prime plays
// actually did over a window — per-day ledger (exact plays + HIT/MISS) plus totals
// (W-L, hit rate, real ROI/edge) and a strong-vs-prime split. kind = all|strong|prime.
export const getGridRecord = (days=14, propType='HRR_over_1.5', kind='all') =>
  get(`/scorecard/grid-record?days=${days}&prop_type=${encodeURIComponent(propType)}&kind=${kind}`)

// Settled top-N-per-market detail for a date (yesterday by default): the exact board
// plays that went in the public record, each with HIT/MISS, actual vs line, and Rating.
// Powers the "Yesterday's Results" drawer in the Results tab. blank date = today ET.
export const getRecordDetail = (date='', top=10) =>
  get(`/free/record-detail?date=${encodeURIComponent(date)}&top=${top}`)

// ── Upcoming matches ─────────────────────────────────────────────────────────
export const getUpcomingMatches = (date) =>
  get(`/games/upcoming?game_date=${date}`)

export const getGameDetail = (homeId, awayId, gameDate) =>
  get(`/games/detail?home_id=${homeId}&away_id=${awayId}&game_date=${gameDate}`)

// AI writeup ("The Read" / Slate Read); returns {narrative: string|null}
export const getGameRead = (cacheKey, facts, kind = 'game') =>
  post(`/games/read`, { cache_key: cacheKey, facts, kind })
